import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { exportCsv } from '@/lib/csv';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';

interface CampaignData {
  id: string;
  name: string;
  provider: string;
  status: string;
  budget: number | null;
  isActive: boolean;
  leadCount: number;
}

interface Props {
  dateFrom: string;
  dateTo: string;
}

const STATUS_BADGE: Record<string, string> = {
  Active: 'bg-emerald-50 text-emerald-700',
  Paused: 'bg-amber-50 text-amber-700',
  Completed: 'bg-slate-100 text-slate-500',
};

export function CampaignReports({ dateFrom, dateTo }: Props) {
  const [data, setData] = useState<CampaignData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/reports/campaigns', { params: { dateFrom, dateTo } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const { sorted, sort, onSort } = useSort(data);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    );
  }

  if (data.length === 0) {
    return <p className="text-sm text-slate-400 py-12 text-center">No campaigns found</p>;
  }

  const chartData = data.filter(c => c.leadCount > 0).slice(0, 10);

  const handleExport = () => {
    exportCsv('campaigns_performance', data, [
      { key: 'name', label: 'Campaign' },
      { key: 'provider', label: 'Provider' },
      { key: 'status', label: 'Status' },
      { key: 'budget', label: 'Budget' },
      { key: 'isActive', label: 'Active' },
      { key: 'leadCount', label: 'Leads' },
    ]);
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>
      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Leads per Campaign</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="leadCount" fill="#6366F1" radius={[4, 4, 0, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <TableHeadRow>
                <SortTh label="Campaign" sortKey="name" sort={sort} onSort={onSort} />
                <SortTh label="Provider" sortKey="provider" sort={sort} onSort={onSort} />
                <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} />
                <SortTh label="Budget" sortKey="budget" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Leads" sortKey="leadCount" sort={sort} onSort={onSort} align="right" />
                <SortTh label="Cost/Lead" sortable={false} align="right" />
              </TableHeadRow>
            </thead>
            <tbody>
              {sorted.map(c => {
                const costPerLead = c.budget && c.leadCount > 0 ? (c.budget / c.leadCount).toFixed(0) : '—';
                return (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.provider}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[c.status] || 'bg-slate-100 text-slate-600'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {c.budget != null ? `$${c.budget.toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-[#6366F1]">{c.leadCount}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{costPerLead !== '—' ? `$${costPerLead}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
